import { useRequestInit, ValidatiePopup, BrowserNavigation, Icon } from "adviesbox-shared";
import classNames from "classnames";
import { FormikContextType } from "formik";
import React, {
  Dispatch,
  MutableRefObject,
  ReactElement,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useState
} from "react";
import { Button } from "react-bootstrap";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { AppDataContext } from "../../../navigation/appdata-context";
import OpenDossierLogContext from "../../open-dossier-log/open-dossier-log-context";
import { SaveFormContext } from "../../save-form/save-form-context";
import { WithSaveData } from "../../utils/save-data";
import { isOkSubmitResult, submit, SubmitResultTypes } from "../../utils/save-validation";
import SaveNavigation from "../save-navigation/save-navigation";
import classes from "./SaveButton.module.scss";
import { saveButtonTextResources } from "./save-button-resources";

type SaveButtonProps = {
  callBack?: () => void;
  preSaveConfirmation?: (path?: string) => Promise<void> | void;
  triggerPreSaveConfirmation?: boolean;
  name?: string;
  initialSaveResult?: SubmitResultTypes; // Voor testen alleen
  setPreviousPath?: Dispatch<SetStateAction<string | null>>;
  context: FormikContextType<any> &
    WithSaveData<any> & {
      berekenSideEffectRef?: MutableRefObject<{ asyncResult: Promise<unknown> }>;
    };
  showSaveRequest?: boolean;
  isCanceled?: boolean;
  propagateDirty?: boolean;
  label?: string;
  forwardRef?: React.Ref<SaveButtonActions>;
};

export interface SaveButtonActions {
  performSave: () => Promise<SubmitResultTypes | null>;
}

const SaveButtonComponent = ({
  history,
  context,
  forwardRef,
  context: { saveData, dirty, isSubmitting },
  callBack,
  preSaveConfirmation,
  triggerPreSaveConfirmation,
  setPreviousPath,
  isCanceled,
  showSaveRequest,
  propagateDirty = true,
  label = saveButtonTextResources(showSaveRequest ? "SaveRequest" : "Save"),
  ...props
}: SaveButtonProps & RouteComponentProps): ReactElement | null => {
  const { setIsSaving, version, setSaveResult: propagateSaveResult } = useContext(SaveFormContext);
  const [lastSave, setLastSave] = useState(version);
  let buttonContent: ReactElement | null = null;
  const {
    menuInfo: { adviseurIds },
    isPageDirty,
    setPageDirty
  } = useContext(AppDataContext);
  const { seRunning, setSaveContext } = useContext(AppDataContext);
  const {
    lezenEnSchrijvenRechtenVestigingen: lezenEnSchrijvenRechten,
    getAdviseurIds,
    adviseurIds: adviseurIdsHuidigeGebruiker,
    adviseurIdsOphalen
  } = useContext(OpenDossierLogContext);
  const { params } = useRequestInit<{ vestiging: string }>();
  const [showValidatiePopup, setShowValidatiePopup] = useState(false);
  const [saveResult, setSaveResult] = useState<SubmitResultTypes>(props.initialSaveResult || "default");
  const [showNavValidatiePopup, setNavValidatiePopup] = useState(false);
  const [prevStateValues, setPrevStateValues] = useState<any>(context.initialValues);

  const scrollToTop = (scroll: boolean): void => {
    if (!scroll) return;
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  const actualSaveData = useCallback(
    async (
      preventReloadNavigation?: boolean,
      skipTriggerPreSaveConfirmation?: boolean
    ): Promise<SubmitResultTypes | null> => {
      if (triggerPreSaveConfirmation && !skipTriggerPreSaveConfirmation) {
        preSaveConfirmation && (await preSaveConfirmation());
        return null;
      }
      const validationResult = await submit(
        saveData,
        context,
        context.berekenSideEffectRef,
        callBack,
        preventReloadNavigation
      );
      setSaveResult(validationResult);
      setShowValidatiePopup(!isOkSubmitResult(validationResult));
      scrollToTop(!isOkSubmitResult(validationResult));
      return validationResult;
    },
    [triggerPreSaveConfirmation, preSaveConfirmation, saveData, context, callBack, setSaveResult, setShowValidatiePopup]
  );

  useImperativeHandle(
    forwardRef,
    () => ({
      performSave: actualSaveData
    }),
    [actualSaveData]
  );

  useEffect(() => {
    if (lastSave === version) return;
    setLastSave(version);
    setIsSaving(true);
    propagateSaveResult(null);
    actualSaveData()
      .then(values => {
        propagateSaveResult(values);
      })
      .finally(() => {
        setIsSaving(false);
      })
      .catch(reason => {
        /* eslint-disable-line no-console */ /* istanbul ignore next */ console.error(reason);
      });
  }, [
    lastSave,
    version,
    setIsSaving,
    triggerPreSaveConfirmation,
    preSaveConfirmation,
    saveData,
    context,
    callBack,
    setSaveResult,
    setShowValidatiePopup,
    propagateSaveResult,
    actualSaveData
  ]);

  /* istanbul ignore next */
  if (adviseurIdsOphalen) {
    getAdviseurIds(params.vestiging);
  }

  /* istanbul ignore next */
  const huidigeGebruiker = adviseurIdsHuidigeGebruiker && adviseurIdsHuidigeGebruiker.map(e => e);
  const gebruikersEigenAangemaaktDossier = adviseurIds && huidigeGebruiker.includes(adviseurIds.toString());

  useEffect(() => {
    if (prevStateValues !== context.values) {
      setPrevStateValues(context.values);
      setSaveContext({
        saveDataFn: /* istanbul ignore next */ async preventReloadNavigation => {
          const result = await actualSaveData(preventReloadNavigation, true);
          return result;
        }
      });
    }
  }, [callBack, context, prevStateValues, saveData, setSaveContext, actualSaveData]);

  useEffect(() => {
    if (propagateDirty && isPageDirty !== context.dirty) {
      setPageDirty(context.dirty);
    }
  }, [propagateDirty, context.dirty, isPageDirty, setPageDirty]);

  if (isSubmitting) {
    buttonContent = (
      <span id={props.name || "save-button"} className={classes.busy}>
        {saveButtonTextResources("Busy")}
      </span>
    );
  } else if (dirty) {
    const isPlatformError = saveResult === "platformError";
    buttonContent = (
      <Button
        disabled={seRunning}
        id={props.name || "save-button"}
        variant={"primary"}
        onClick={async (): Promise<void> => {
          // trigger het opslaan
          setLastSave(version - 1);
        }}
      >
        <Icon name="save" className="mr-2"></Icon>
        {isPlatformError ? saveButtonTextResources("TryAgain") : label}
      </Button>
    );
  } else {
    buttonContent = (
      <span
        id={props.name || "save-button"}
        className={classNames({
          [classes.no_save]: saveResult === "default",
          [classes.saved]: saveResult === "completed",
          "pb-2": true
        })}
      >
        {showSaveRequest ? saveButtonTextResources("SaveSuccesRequest") : saveButtonTextResources("SaveSucces")}
      </span>
    );
  }

  return (
    <>
      {(!lezenEnSchrijvenRechten ||
        lezenEnSchrijvenRechten === "LezenEnSchrijven" ||
        gebruikersEigenAangemaaktDossier) && (
        <>
          {buttonContent}
          <SaveNavigation
            history={history}
            triggerPreSaveConfirmation={triggerPreSaveConfirmation}
            preSaveConfirmation={preSaveConfirmation}
            saveData={saveData}
            setValidatiePopup={setNavValidatiePopup}
            setSaveResultText={setSaveResult}
            showValidatiePopup={showNavValidatiePopup}
            callBack={callBack}
            setPreviousPath={setPreviousPath}
            isCanceled={isCanceled}
            onHide={async (): Promise<void> => {
              setNavValidatiePopup(false);
              if (context.errors) {
                TouchAllFieldsWithErrors(context.errors, context.setFieldTouched);
                await context.validateForm();
              }
            }}
          />
          <ValidatiePopup
            infotekst={saveResult}
            show={showValidatiePopup}
            onHide={async (): Promise<void> => {
              setShowValidatiePopup(false);
              if (context.errors) {
                TouchAllFieldsWithErrors(context.errors, context.setFieldTouched);
                await context.validateForm();
              }
            }}
          />
          <BrowserNavigation
            preventWarning={triggerPreSaveConfirmation || false}
            dirty={dirty}
            modalShow={showNavValidatiePopup}
          />
        </>
      )}
    </>
  );
};

export const SaveButton = withRouter(SaveButtonComponent);

// Markeert alle velden met errors als 'touched' om validatiefouten te tonen vóór het opslaan.
export function TouchAllFieldsWithErrors(
  errors: any,
  setFieldTouched: (field: string, touched?: boolean, shouldValidate?: boolean) => void,
  prefix = ""
): void {
  Object.keys(errors).forEach(key => {
    const fieldPath = prefix ? `${prefix}.${key}` : key;
    if (typeof errors[key] === "object" && errors[key] !== null && !Array.isArray(errors[key])) {
      // Markeer recursief alle geneste velden als 'touched'
      TouchAllFieldsWithErrors(errors[key], setFieldTouched, fieldPath);

      // Als het veld eindigt op 'datum', markeer dan ook het kind met de suffix '.datum' als 'touched'
      if (fieldPath.endsWith("datum")) {
        Object.keys(errors[key]).forEach(childKey => {
          if (childKey.endsWith("datum")) {
            setFieldTouched(`${fieldPath}.${childKey}`, true, false);
          }
        });
      }
    } else if (Array.isArray(errors[key])) {
      // Als de fout een array is, markeer dan elke index als 'touched'
      errors[key].forEach((_item: any, idx: number) => {
        const arrayFieldPath = `${fieldPath}[${idx}]`;
        setFieldTouched(arrayFieldPath, true, false);
      });
    } else {
      setFieldTouched(fieldPath, true, false);

      // Als het veld eindigt op 'datum', markeer dan ook het kind met de suffix '.datum' als 'touched'
      if (fieldPath.endsWith("datum")) {
        setFieldTouched(`${fieldPath}.datum`, true, false);
      }
    }
  });
}
